import { getUID } from '@redviking/argonaut-util/src/getUID';

import { UiFlowNodeControl } from '../../controls/UiFlowNodeControl';
import { UiFlowNodeInput } from '../../sockets/UiFlowNodeInput';
import type { UiNodeSpec } from './UiNodeSpec';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';
import { accessor } from 'src/store';
import { getSocketTypeForVarId } from '../socket-var-helpers';

export const varWriteMultipleSpec: UiNodeSpec<'varWriteMultiple'> = {
    requiredSockets: [ 'trigger' ],
    cfg: () => ({ controlValues: { vars: [] }, id: getUID(), kind: 'varWriteMultiple', disabled: false }),
    controls: getNodeCfg => ({
        vars: {
            cfg: {
                kind: 'button',
                label: 'Add Variable',
                onClick: () => {
                    const nodeCfg = getNodeCfg();
                    createNodeSocket({
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                vars: [
                                    ...nodeCfg.controlValues.vars,
                                    '',
                                ],
                            },
                        },
                    });
                },
            },
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.vars,
            index: 1,
        },
    }),
    inputs: nodeCfg => {
        const trigger: UiFlowNodeInput.Opts<'event'> = { socket: 'event', label: 'Trigger', index: 0 };
        const inputs: ReturnType<UiNodeSpec<'varWriteMultiple'>['inputs']> = {
            trigger,
        };

        for (let i = 0; i < nodeCfg.controlValues.vars.length; i++) {
            const control: UiFlowNodeControl.Opts<string> = {
                cfg: {
                    kind: 'selectVar',
                    onSelect (selectedVariableId) {
                        createNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    vars: nodeCfg.controlValues.vars.map((item, itemIdx) => itemIdx === i ? selectedVariableId : item),
                                },
                            },
                        });
                    },
                    onDelete () {
                        removeNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    vars: [
                                        ...nodeCfg.controlValues.vars.slice(0, i),
                                        ...nodeCfg.controlValues.vars.slice(i + 1),
                                    ],
                                },
                            },
                            direction: 'input',
                            removalMethod: {
                                index: i,
                                prefix: 'var',
                                kind: 'indexedIdentifier',
                            },
                        });
                    },
                },
                nodeCfg,
                value: nodeCfg.controlValues.vars[i],
                socketDefault: false,
            };
            inputs[`var${i}`] = ({
                control,
                socket: getSocketTypeForVarId(nodeCfg.controlValues.vars[i]),
            });
        }

        return inputs;
    },
    kindLabel: 'Variable Write Multiple',
    nodeCategory: 'variable',
    outputs: () => ({ next: { socket: 'event', label: 'Next' } }),
    validator: (node) => {
        const errors: Record<string, string> = {};
        
        if (!node.controlValues.vars.length) {
            errors.vars = 'At least one variable is required';
        } else {
            // Validate each variable in the array
            const allAppletVariables = accessor.appletDesign.allAppletVariables;
            const invalidVars = node.controlValues.vars.filter(varId => !allAppletVariables[varId]);
            if (invalidVars.length > 0) {
                errors.vars = 'One or more variables are not valid';
            }
        }
        
        return Object.keys(errors).length ? errors : true;
    },
};
